/**
 * StudioVariations
 *
 * Variation Studio - Generates image variations where the product stays
 * identical but scene/background/context changes.
 *
 * Route: /v2/tools/studio/variations
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageIcon, Settings, Upload } from 'lucide-react';
import { useSimpleGallery } from '@/hooks/useGalleryV2';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { galleryImagesToLegacy } from '@/lib/api/gallery-v2';
import { MasterImageTabs } from '@/components/v2/tools/generation/MasterImageTabs';
import { AssetContextPanelV2 } from '@/components/v2/tools/generation/AssetContextPanelV2';
import { ImagePreviewPanel } from '@/components/v2/tools/generation/ImagePreviewPanel';
import { MiniGalleryPreview } from '@/components/v2/tools/generation/MiniGalleryPreview';
import { VariationsSettingsPanel, type VariationsSettings } from '@/components/v2/tools/generation/VariationsSettingsPanel';
import { useVariations } from '@/hooks/useVariations';
import { toast } from 'sonner';
import type { Image } from '@/types';

const LEFT_PANEL_WIDTH_PERCENT = 0.21;
const MIN_LEFT_PANEL_WIDTH = 280;
const MAX_LEFT_PANEL_WIDTH = 450;
const RIGHT_PANEL_WIDTH_PERCENT = 0.20;
const MIN_RIGHT_PANEL_WIDTH = 320;
const MAX_RIGHT_PANEL_WIDTH = 480;

const getInitialLeftPanelWidth = () => {
  const vw = window.innerWidth;
  return Math.min(Math.max(vw * LEFT_PANEL_WIDTH_PERCENT, MIN_LEFT_PANEL_WIDTH), MAX_LEFT_PANEL_WIDTH);
};

const getInitialRightPanelWidth = () => {
  const vw = window.innerWidth;
  return Math.min(Math.max(vw * RIGHT_PANEL_WIDTH_PERCENT, MIN_RIGHT_PANEL_WIDTH), MAX_RIGHT_PANEL_WIDTH);
};

export function StudioVariations() {
  const { t } = useTranslation();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [mobilePanel, setMobilePanel] = useState<'assets' | 'preview' | 'settings'>('settings');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [imageInfo, setImageInfo] = useState<{ width: number; height: number; format: string; size: number } | null>(null);
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [leftPanelTab, setLeftPanelTab] = useState<'upload' | 'gallery' | 'product'>('upload');
  const [generatedImage, setGeneratedImage] = useState<Image | null>(null);
  const [generationStartTime, setGenerationStartTime] = useState<Date | undefined>(undefined);

  // Resizable panels
  const [leftPanelWidth, setLeftPanelWidth] = useState(getInitialLeftPanelWidth());
  const [rightPanelWidth] = useState(getInitialRightPanelWidth());
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(getInitialLeftPanelWidth());

  const { images: galleryImages, refetch } = useSimpleGallery({ page: 1, perPage: 12 });
  const variationMutation = useVariations();

  const handleFileChange = useCallback((file: File | null) => {
    setSelectedFile(file);
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);
      const img = new window.Image();
      img.onload = () => {
        setImageInfo({ width: img.width, height: img.height, format: file.type.split('/')[1].toUpperCase(), size: file.size });
      };
      img.src = previewUrl;
    } else {
      setPreview(null);
      setImageInfo(null);
    }
  }, []);

  const handleGenerate = useCallback(async (settings: VariationsSettings) => {
    if (!selectedFile) return;

    setGenerationStartTime(new Date());
    setGeneratedImage(null);

    try {
      const result = await variationMutation.mutateAsync({
        image: selectedFile,
        user_context: settings.user_context,
        brand_id: selectedBrandId || undefined,
        ratio: settings.ratio,
        resolution: settings.resolution,
        image_count: settings.image_count,
        image_format: settings.image_format,
        png_quality: settings.png_quality,
      });

      if (result.success && result.images.length > 0) {
        toast.success(`${result.images.length} Variation(en) erstellt (${result.total_credits_used} Credits)`);
        const refetchResult = await refetch();
        if (refetchResult.data?.images?.length) {
          const legacyImages = galleryImagesToLegacy(refetchResult.data.images);
          if (legacyImages.length > 0) setGeneratedImage(legacyImages[0]);
        }
      } else {
        toast.error(result.error || t('toast.error.variationFailed'));
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('toast.error.variationFailed'));
    } finally {
      setGenerationStartTime(undefined);
    }
  }, [selectedFile, selectedBrandId, variationMutation, refetch, t]);

  const handleBottomGalleryImageClick = useCallback(async (imageId: string) => {
    setLeftPanelTab('gallery');
    const legacyImages = galleryImagesToLegacy(galleryImages);
    const image = legacyImages.find(img => img.id === imageId);
    if (!image?.image_url) return;

    try {
      const response = await fetch(image.image_url);
      const blob = await response.blob();
      handleFileChange(new File([blob], `gallery-${imageId}.jpg`, { type: blob.type }));
    } catch (error) {
      console.error('Error loading gallery image:', error);
    }
  }, [galleryImages, handleFileChange]);

  // Resize handler
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = leftPanelWidth;
    setIsResizing(true);
  }, [leftPanelWidth]);

  useEffect(() => {
    if (!isResizing) return;
    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - resizeStartX.current;
      setLeftPanelWidth(Math.min(Math.max(resizeStartWidth.current + delta, MIN_LEFT_PANEL_WIDTH), MAX_LEFT_PANEL_WIDTH));
    };
    const handleMouseUp = () => setIsResizing(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const assetPanel = (
    <AssetContextPanelV2
      selectedFile={selectedFile}
      preview={preview}
      onFileChange={handleFileChange}
      selectedBrandId={selectedBrandId}
      onBrandChange={setSelectedBrandId}
      activeTab={leftPanelTab}
      onTabChange={setLeftPanelTab}
    />
  );

  const previewPanel = (
    <ImagePreviewPanel
      preview={preview}
      imageInfo={imageInfo}
      isGenerating={variationMutation.isPending}
      generationStartTime={generationStartTime}
      generatedImage={generatedImage}
      onNewGeneration={() => {}}
    />
  );

  const settingsPanel = (
    <VariationsSettingsPanel
      onGenerate={handleGenerate}
      isGenerating={variationMutation.isPending}
      hasImage={!!selectedFile}
    />
  );

  /* ---- Mobile Layout ---- */
  if (!isDesktop) {
    const MOBILE_TABS = [
      { id: 'settings' as const, label: 'Generieren', icon: Settings },
      { id: 'assets' as const, label: 'Assets', icon: Upload },
      { id: 'preview' as const, label: 'Vorschau', icon: ImageIcon },
    ];

    return (
      <div className="flex flex-col h-[calc(100vh-56px-64px)]">
        <MasterImageTabs activeTab="variations" onTabChange={() => {}} />

        <div className="flex border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
          {MOBILE_TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setMobilePanel(id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
                mobilePanel === id
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                  : 'border-transparent text-neutral-500 dark:text-neutral-400'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          {mobilePanel === 'assets' && assetPanel}
          {mobilePanel === 'preview' && previewPanel}
          {mobilePanel === 'settings' && (
            <div className="bg-neutral-50 dark:bg-neutral-900/50">
              {settingsPanel}
            </div>
          )}
        </div>
      </div>
    );
  }

  /* ---- Desktop Layout ---- */
  return (
    <>
      <div className="flex flex-col h-[98%]">
        <div className="mode-tabs-section">
          <MasterImageTabs activeTab="variations" onTabChange={() => {}} />
        </div>

        <div className="flex flex-1 overflow-hidden">
          <aside
            className="flex-shrink-0 border-r border-neutral-200 dark:border-neutral-800 relative"
            style={{ width: `${leftPanelWidth}px` }}
          >
            {assetPanel}
            <div
              className={`absolute top-0 right-0 bottom-0 w-1 cursor-col-resize hover:bg-orange-500 transition-colors ${isResizing ? 'bg-orange-500' : 'bg-transparent'}`}
              onMouseDown={handleResizeStart}
              title="Breite anpassen"
            />
          </aside>

          <section className="flex-1 border-r border-neutral-200 dark:border-neutral-800">
            {previewPanel}
          </section>

          <aside
            className="flex-shrink-0 bg-neutral-50 dark:bg-neutral-900/50 overflow-y-auto"
            style={{ width: `${rightPanelWidth}px` }}
          >
            {settingsPanel}
          </aside>
        </div>
      </div>

      <MiniGalleryPreview
        onImageClick={handleBottomGalleryImageClick}
      />
    </>
  );
}
